<template>
    <VJoyTemplate>
        <template #template-title>{{ $t('projectclient.projectview.contract-amendment.title', [projectInformation?.contract.subject]) }}</template>
        <template #template-subtitle>
            <VJoyHighlight level="warning" display-icon class="joy-mt-6">
                {{ $t('projectclient.projectview.contract-amendment.highlight.modification') }}
            </VJoyHighlight>
            <VJoyHighlight level="info" display-icon class="joy-mt-6">
                {{ $t('projectclient.projectview.contract-amendment.highlight.invoicing-model') }}
            </VJoyHighlight>
        </template>
        <template #template-main>
            <VJoyPanel v-if="showCurrentTermsPanel">
                <template #panel-title>{{ $t('projectclient.projectview.contract-amendment.panel.current.title') }}</template>
                <template #panel-body>
                    <VJoyText preset="primary-450" class="joy-mb-2">
                        {{ $t('projectclient.projectview.contract-amendment.panel.subtitle') }}
                    </VJoyText>
                    <span
                        v-if="currentAcceptanceDate && currentTermsUrl"
                        v-html="
                            $t('projectclient.projectview.contract-amendment.panel.current.description', {
                                0: escapeHtml(currentTermsUrl),
                                1: currentAcceptanceDate,
                            })
                        "
                    />
                </template>
            </VJoyPanel>
            <VJoyWrapper v-if="showCurrentTermsPanel && newTermsUrl" direction="row" justify="center" align="center" class="joy-my-4">
                <VJoyIcon class="contract-modification__between-panels-arrow" size="small" name="arrow-right" />
            </VJoyWrapper>
            <VJoyPanel v-if="newTermsUrl">
                <template #panel-title>{{ $t('projectclient.projectview.contract-amendment.panel.new.title') }}</template>
                <template #panel-body>
                    <div>
                        <VJoyText preset="primary-450" class="joy-mb-2">
                            {{ $t('projectclient.projectview.contract-amendment.panel.subtitle') }}
                        </VJoyText>
                        <VJoyText preset="primary-400">
                            {{ $t('projectclient.projectview.contract-amendment.panel.new.description') }}
                        </VJoyText>
                    </div>
                    <div>
                        <ValidationError :errors="r$.$errors.termsAndConditions" margin>
                            <VJoyCheckbox
                                v-model:checked="r$.$value.termsAndConditions"
                                :invalid="r$.termsAndConditions.$error"
                                data-testid="contract-amendment-terms-and-conditions"
                            >
                                <span
                                    v-html="
                                        $t('projectclient.projectview.contract-amendment.panel.new.checkbox', {
                                            0: escapeHtml(newTermsUrl ?? ''),
                                        })
                                    "
                                ></span>
                            </VJoyCheckbox>
                        </ValidationError>
                    </div>
                </template>
            </VJoyPanel>
            <VJoyWrapper direction="row" justify="center" class="joy-mt-6">
                <VJoyButton variant="main" size="medium" data-testid="submit-amendment" :loading="isSubmitting" @click="handleModification">
                    {{ $t('projectclient.projectview.contract-amendment.cta.accept-modification') }}
                </VJoyButton>
            </VJoyWrapper>
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {checked, withMessage} from '@regle/rules';
    import {useRoute} from '@typed-router';
    import {storeToRefs, navigateTo, useTranslation, useFormat} from '#imports';
    import {useRegle} from '@malt/forms';
    import {escapeHtml} from '@malt/sanitize';
    import {VJoyButton, VJoyTemplate, VJoyHighlight, VJoyPanel, VJoyCheckbox, VJoyText, VJoyWrapper, VJoyIcon} from '@maltjoy/core-vue';
    import {useProjectInformationStore} from '~/common/stores';
    import ValidationError from '~/common/ui/ValidationError.vue';
    import type {
        ContractDirectModeResource,
        ContractInvoicingModeResource,
        ContractSubcontractingModeResource,
        ContractTripartiteModeResource,
    } from '~/openapi';

    const route = useRoute('project-projectId-contract-amendment');
    const {formatDate} = useFormat();
    const {t} = useTranslation();
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, terms} = storeToRefs(projectInformationStore);

    const isSubmitting = ref(false);

    function hasProjectOrClientTerms(mode: ContractInvoicingModeResource | undefined): boolean {
        if (!mode) {
            return false;
        }
        if (mode.type === 'TRIPARTITE') {
            return Boolean((mode as ContractTripartiteModeResource).projectTerms);
        }
        if (mode.type === 'SUBCONTRACTING' || mode.type === 'DIRECT') {
            return Boolean((mode as ContractDirectModeResource | ContractSubcontractingModeResource).clientTerms);
        }
        return false;
    }

    function termsUrlFromInvoicingMode(mode: ContractInvoicingModeResource | undefined): string | undefined {
        if (!mode) {
            return undefined;
        }
        if (mode.type === 'TRIPARTITE') {
            return (mode as ContractTripartiteModeResource).projectTerms?.url;
        }
        if (mode.type === 'SUBCONTRACTING') {
            return (mode as ContractSubcontractingModeResource).clientTerms?.url;
        }
        if (mode.type === 'DIRECT') {
            return (mode as ContractDirectModeResource).clientTerms?.url;
        }
        return undefined;
    }

    const currentAppliedContractTerm = computed(() => {
        const list = terms.value?.current?.contractTerms;
        if (!list?.length) {
            return undefined;
        }
        return list[list.length - 1];
    });

    const currentTermsUrl = computed(() => termsUrlFromInvoicingMode(currentAppliedContractTerm.value?.invoicingMode));

    const showCurrentTermsPanel = computed(() => hasProjectOrClientTerms(currentAppliedContractTerm.value?.invoicingMode));

    const currentAcceptanceDate = computed(() => {
        const raw = currentAppliedContractTerm.value?.clientAcceptedAt;
        if (!raw) {
            return null;
        }
        return formatDate(raw, {dateStyle: 'medium'});
    });

    const newTermsUrl = computed(() => termsUrlFromInvoicingMode(terms.value?.pending));

    const state = ref({
        termsAndConditions: false,
    });

    const {r$} = useRegle(state, {
        termsAndConditions: {
            $rewardEarly: true,
            checked: withMessage(checked, t('projectclient.projectview.contract-amendment.checkbox.error.required')),
        },
    });
    async function handleModification() {
        const {valid} = await r$.$validate();
        if (!valid) {
            return;
        }
        isSubmitting.value = true;
        try {
            const projectId = route.params.projectId as string;
            await projectInformationStore.updateTerms(projectId);
            await navigateTo({
                name: 'project-projectId-index-activity',
                params: {projectId},
            });
        } catch {
        } finally {
            isSubmitting.value = false;
        }
    }
</script>

<style lang="scss" scoped>
    .contract-modification__between-panels-arrow {
        transform: rotate(90deg);
    }
</style>
